var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Banner, Button, FieldWrapper, FormField, FormGrid, Page, SelectionList, Tag, } from '@dixa/lib-shared-habla';
import { config } from 'lib-shared-backend-config';
import { useForm, useWatch } from 'react-hook-form';
import { authApi } from 'apis/auth';
import { HookFormSelectionList, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { isResponseWithData } from 'utils/apis/axios';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { validateName } from 'utils/formValidator';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { SettingsIntegrationsDocumentationButton } from '../../../../components/documentationButton/documentationButton';
import { pathToApiTokensSettings } from '../../settingsIntegrationsApiTokens.view';
import { apiTokenTypesByVersion, dixaApiToken, integrationsApiToken, } from '../../utils/tokenType';
const defaultTokenVersion = '2';
/** Settings page to create a new API token. */
export const SettingsIntegrationsApiTokensCreateView = () => {
    const navigate = useAppNavigate();
    const notify = useNotifications();
    const { allowIntegrationApiTokenUsage } = useFeatureFlag([
        'allow-integration-api-token-usage',
    ]);
    const apiTokenTypes = [
        dixaApiToken,
        ...(allowIntegrationApiTokenUsage ? [integrationsApiToken] : []),
    ];
    const [createApiToken, { isLoading: isCreating }] = authApi.useCreateApiTokenMutation();
    const { control, handleSubmit } = useForm({
        defaultValues: {
            name: '',
            scopes: ['FULL_ACCESS'],
        },
        mode: 'onChange',
    });
    const selectedTokenVersion = useWatch({
        control: control,
        name: 'version',
    });
    const onSubmit = ({ name, scopes, version, }) => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        const response = yield createApiToken({
            name,
            scopes,
            version: version && (version === null || version === void 0 ? void 0 : version.length) > 0 ? version[0] : defaultTokenVersion,
        }).unwrap();
        if (isResponseWithData(response)) {
            notify({
                title: 'Success',
                text: `"${(_a = response.data) === null || _a === void 0 ? void 0 : _a.name}" has been created.`,
                icon: 'i-done',
                time: 5000,
            }, NotificationType.Success);
            navigate({
                path: `${pathToApiTokensSettings}/:id`,
                params: {
                    path: {
                        id: response.data.id,
                    },
                },
            });
        }
        else {
            notify({
                title: 'Error while creating the API token.',
            }, NotificationType.Error);
        }
    });
    return (_jsxs(Page.Section, Object.assign({ title: "Create API token", titleMeta: _jsx(SettingsIntegrationsDocumentationButton, { onClick: () => window.open(`https://${config.domain.integrations}/openapi`, '_blank', 'noopener,noreferrer') }), controls: _jsx(Button, Object.assign({ disabled: isCreating, isLoading: isCreating, onClick: handleSubmit(onSubmit) }, { children: "Save changes" })) }, { children: [_jsx("form", Object.assign({ onSubmit: handleSubmit(onSubmit) }, { children: _jsxs(FormGrid, { children: [_jsx(FormField, Object.assign({ span: 3 }, { children: _jsx(HookFormText, { control: control, label: "Name", name: 'name', rules: {
                                    required: {
                                        value: true,
                                        message: 'Please add a name',
                                    },
                                    validate: validateName,
                                }, componentProps: {
                                    placeholder: 'Insert name',
                                } }) })), _jsx(FormField, { span: 3 }), _jsx(FormField, Object.assign({ span: 3 }, { children: allowIntegrationApiTokenUsage ? (_jsx(HookFormSelectionList, Object.assign({ control: control, label: "Version", name: 'version', rules: {
                                    required: {
                                        value: true,
                                        message: 'Please select a version',
                                    },
                                }, meta: {
                                    warning: (selectedTokenVersion === null || selectedTokenVersion === void 0 ? void 0 : selectedTokenVersion[0]) === '1'
                                        ? 'You are about to create a token for a deprecated API'
                                        : null,
                                }, componentProps: {
                                    clearable: true,
                                    placeholder: 'Select token version',
                                    getItemTitleByValue: (version) => (_jsx(Tag, Object.assign({ color: apiTokenTypesByVersion[version].color }, { children: apiTokenTypesByVersion[version].label }))),
                                } }, { children: apiTokenTypes.map((token) => (_jsx(SelectionList.Item, Object.assign({ value: token.version }, { children: _jsx(Tag, Object.assign({ color: token.color }, { children: token.label })) }), token.type))) }))) : (_jsx(FieldWrapper, Object.assign({ label: "Version" }, { children: _jsx(Tag, Object.assign({ color: dixaApiToken.color }, { children: dixaApiToken.label })) }))) })), _jsx(FormField, { span: 3 })] }) })), _jsx(Banner, { variant: "announcement", headline: "Note when creating a new API token", description: _jsx(_Fragment, { children: "The new token is bound to the Administrator account that created it which gives integrations full access to your Dixa account. Be very careful when handling it and store it securely." }) })] })));
};
